import { Chess } from 'chess.js';

const FILES = 'abcdefgh';
const COMPLETED_GAME_FIELDS = [
  'win_as_white',
  'win_as_black',
  'lost_as_white',
  'lost_as_black',
];

export function completedGameCount(user) {
  if (!user) return 0;

  return COMPLETED_GAME_FIELDS.reduce(
    (total, field) => total + (Number(user[field]) || 0),
    0
  );
}

function findKing(game, color) {
  for (const file of FILES) {
    for (let rank = 1; rank <= 8; rank += 1) {
      const square = `${file}${rank}`;
      const piece = game.get(square);
      if (piece && piece.type === 'k' && piece.color === color) {
        return square;
      }
    }
  }

  return null;
}

function squaresAreAdjacent(first, second) {
  return (
    Math.abs(FILES.indexOf(first[0]) - FILES.indexOf(second[0])) <= 1
    && Math.abs(Number(first[1]) - Number(second[1])) <= 1
  );
}

function nextStep(start, end, isKnight) {
  const startFile = FILES.indexOf(start[0]);
  const startRank = Number(start[1]);
  const endFile = FILES.indexOf(end[0]);
  const endRank = Number(end[1]);

  let fileDirection = Math.sign(endFile - startFile);
  let rankDirection = Math.sign(endRank - startRank);

  if (startFile === endFile) fileDirection = 0;
  if (startRank === endRank) rankDirection = 0;
  if (isKnight) rankDirection = 0;

  const file = endFile + fileDirection;
  const rank = endRank + rankDirection;
  if (file < 0 || file > 7 || rank < 1 || rank > 8) return null;

  return `${FILES[file]}${rank}`;
}

function simulateSumoMove(game, sourceSquare, targetSquare) {
  const simulatedGame = new Chess(game.fen());
  const movingPiece = simulatedGame.get(sourceSquare);
  const targetPiece = simulatedGame.get(targetSquare);
  if (!movingPiece) return null;

  if (targetPiece && movingPiece.type !== 'k') {
    const displacedSquare = nextStep(
      sourceSquare,
      targetSquare,
      movingPiece.type === 'n'
    );

    if (displacedSquare && simulatedGame.get(displacedSquare)) return null;
    if (displacedSquare) simulatedGame.put(targetPiece, displacedSquare);
  }

  try {
    const result = simulatedGame.move({
      from: sourceSquare,
      to: targetSquare,
      promotion: 'q',
    });
    return result ? simulatedGame : null;
  } catch (error) {
    return null;
  }
}

export function shouldWarnAboutKingCapture({
  game,
  user,
  sourceSquare,
  targetSquare,
  lastMoveAsString,
}) {
  if (!game || completedGameCount(user) >= 4) return false;

  const lastMove = lastMoveAsString ? lastMoveAsString.split(',') : [];
  if (
    lastMove.length > 2
    && lastMove[1] === targetSquare
    && lastMove[2] === sourceSquare
  ) {
    return false;
  }

  const movingPiece = game.get(sourceSquare);
  if (!movingPiece) return false;

  const simulatedGame = simulateSumoMove(game, sourceSquare, targetSquare);
  if (!simulatedGame) return false;

  const opponentColor = movingPiece.color === 'w' ? 'b' : 'w';
  const opponentKingSquare = findKing(simulatedGame, opponentColor);
  if (
    !opponentKingSquare
    || !squaresAreAdjacent(opponentKingSquare, targetSquare)
  ) {
    return false;
  }

  const kingCaptureGame = new Chess(simulatedGame.fen());
  try {
    const kingCapture = kingCaptureGame.move({
      from: opponentKingSquare,
      to: targetSquare,
    });
    return Boolean(kingCapture && kingCapture.piece === 'k');
  } catch (error) {
    return false;
  }
}
